var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { InputAdornment, Select } from '@mui/material';
import { FlagImage, defaultCountries, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Divider, ListSubheader, MenuItem, TextField, Typography } from '@emburse/ui-core';
export const PhoneNumberInput = (_a) => {
    var { value, disabled, inputProps, onChange, preferredCountries, defaultCountry, errorMessage, disableCountryDropdown, excludeCountries, isValid } = _a, restProps = __rest(_a, ["value", "disabled", "inputProps", "onChange", "preferredCountries", "defaultCountry", "errorMessage", "disableCountryDropdown", "excludeCountries", "isValid"]);
    const [numberValidationError, setNumberValidationError] = React.useState('');
    const defaultCountriesAsMap = React.useRef(defaultCountries.reduce((acc, country) => {
        const parsedCountry = parseCountry(country);
        acc[parsedCountry.iso2] = {
            parsedCountry,
            country
        };
        return acc;
    }, {}));
    // handle exclusion and disableCountryDropdown logic
    const [allowedCountryList, setAllowedCountryList] = React.useState(defaultCountries);
    React.useEffect(() => {
        var _a;
        let allowedCountries;
        if (disableCountryDropdown) {
            const allowedCountry = (_a = defaultCountriesAsMap.current[defaultCountry || 'us']) === null || _a === void 0 ? void 0 : _a.country;
            allowedCountries = [allowedCountry];
        }
        else if (excludeCountries) {
            const excludeSet = new Set(excludeCountries);
            const filteredList = defaultCountries.filter((c) => {
                const parsedCountry = parseCountry(c);
                return !excludeSet.has(parsedCountry.iso2);
            });
            allowedCountries = filteredList;
        }
        if (allowedCountries) {
            setAllowedCountryList(allowedCountries);
        }
    }, [excludeCountries, disableCountryDropdown, defaultCountry]);
    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
        defaultCountry: defaultCountry || 'us',
        value,
        countries: allowedCountryList,
        onChange: (data) => {
            var _a, _b, _c;
            const countryCodeLength = ((_b = (_a = data === null || data === void 0 ? void 0 : data.country) === null || _a === void 0 ? void 0 : _a.dialCode) === null || _b === void 0 ? void 0 : _b.length) || 0;
            if (((_c = data.phone) === null || _c === void 0 ? void 0 : _c.length) <= countryCodeLength) {
                return;
            }
            onChange(data.phone);
            if (isValid) {
                const phoneNumber = data.phone.replace(/\D/g, '');
                if (!isValid(phoneNumber)) {
                    setNumberValidationError('Invalid phone number');
                }
                else {
                    setNumberValidationError('');
                }
            }
        }
    });
    // the user country is part of the exclusion list or is not allowed
    React.useEffect(() => {
        if (!country) {
            const defaultCountryIso2 = defaultCountry || 'us';
            if (!(excludeCountries === null || excludeCountries === void 0 ? void 0 : excludeCountries.includes(defaultCountryIso2))) {
                setCountry(defaultCountryIso2);
            }
            else {
                // if the default country is excluded, fallback to US
                setCountry('us');
            }
        }
    }, [country]);
    // handle input parsing
    const textInputOnChange = (event) => {
        const newValue = event.target.value || '';
        const countryDialCode = (country === null || country === void 0 ? void 0 : country.dialCode) || '';
        // prevent deleting the country code
        if (newValue.length <= countryDialCode.length + 1) {
            event.target.value = `+${countryDialCode}`;
        }
        else if (!newValue.startsWith(`+${countryDialCode}`)) {
            // prevent manually entering a different country code
            const onlyDigits = newValue.replace(/\D/g, ''); // ensure we can still process weird input like ++1
            if (!onlyDigits.startsWith(countryDialCode)) {
                if (onlyDigits.slice(1, countryDialCode.length + 1) === countryDialCode) {
                    // user just added a digit to the front
                    const newNumber = `${onlyDigits[0]}${onlyDigits.slice(countryDialCode.length + 1)}`;
                    event.target.value = `+${countryDialCode}${newNumber}`;
                }
                else {
                    // user copy pasted or something, force value instead
                    event.target.value = `+${countryDialCode}${onlyDigits}`;
                }
            }
        }
        handlePhoneValueChange(event);
    };
    React.useEffect(() => {
        if (((value === null || value === void 0 ? void 0 : value.length) || 0) <= 1) {
            setCountry(country === null || country === void 0 ? void 0 : country.iso2);
        }
    }, [value]);
    return (_jsx(TextField, Object.assign({ variant: "outlined", label: "Phone number", color: "primary", placeholder: "phone_number", value: inputValue, onChange: textInputOnChange, type: "tel", inputRef: inputRef, InputProps: Object.assign({ startAdornment: (_jsx(InputAdornment, Object.assign({ position: "start" }, { children: _jsxs(Select, Object.assign({ "data-testid": "phone-country-select", MenuProps: {
                        style: {
                            height: '300px',
                            width: '360px',
                            top: '10px',
                            left: '-34px'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                        }
                    }, sx: {
                        width: 'max-content',
                        // Remove default outline (display only on focus)
                        fieldset: {
                            display: 'none'
                        },
                        '&.Mui-focused:has(div[aria-expanded="false"])': {
                            fieldset: {
                                display: 'block'
                            }
                        },
                        // Update default spacing
                        '.MuiSelect-select': {
                            padding: '8px',
                            paddingRight: '24px !important'
                        },
                        svg: {
                            right: 0
                        }
                    }, disabled: disableCountryDropdown, value: country === null || country === void 0 ? void 0 : country.iso2, onChange: (e) => setCountry(e.target.value), renderValue: (iso2Val) => _jsx(FlagImage, { iso2: iso2Val, style: { display: 'flex' } }) }, { children: [((preferredCountries === null || preferredCountries === void 0 ? void 0 : preferredCountries.length) || 0) >= 1 &&
                            (preferredCountries === null || preferredCountries === void 0 ? void 0 : preferredCountries.map((iso2) => {
                                if (!iso2) {
                                    return null;
                                }
                                const preferredParsedCountry = defaultCountriesAsMap.current[iso2].parsedCountry;
                                if (!preferredParsedCountry) {
                                    return null;
                                }
                                return (_jsxs(MenuItem, Object.assign({ value: preferredParsedCountry.iso2 }, { children: [_jsx(FlagImage, { iso2: preferredParsedCountry.iso2, style: { marginRight: '8px' } }), _jsx(Typography, Object.assign({ marginRight: "8px" }, { children: preferredParsedCountry.name })), _jsxs(Typography, Object.assign({ color: "gray" }, { children: ["+", preferredParsedCountry.dialCode] }))] }), preferredParsedCountry.iso2));
                            })), ((preferredCountries === null || preferredCountries === void 0 ? void 0 : preferredCountries.length) || 0) >= 1 && (_jsx(ListSubheader, { children: _jsx(Divider, {}) })), allowedCountryList.map((c) => {
                            const parsedCountry = parseCountry(c);
                            return (_jsxs(MenuItem, Object.assign({ value: parsedCountry.iso2 }, { children: [_jsx(FlagImage, { iso2: parsedCountry.iso2, style: { marginRight: '8px' } }), _jsx(Typography, Object.assign({ marginRight: "8px" }, { children: parsedCountry.name })), _jsxs(Typography, Object.assign({ color: "gray" }, { children: ["+", parsedCountry.dialCode] }))] }), parsedCountry.iso2));
                        })] })) }))), sx: {
                height: '40px'
            } }, inputProps), fullWidth: true, error: errorMessage || numberValidationError, helperText: errorMessage || numberValidationError }, restProps)));
};
